// Zugriff auf den SRS-Studio-Server. In der Entwicklung läuft alles über den Vite-Proxy (/api, /yjs, /signal).
import type { Project, SplitSheet, TurnCredentials, User, Contribution } from '@srs/shared';
import { useStudio } from '../state/store.ts';

export type ProjectSummary = Pick<Project, 'id' | 'name' | 'updatedAt' | 'bpm' | 'key' | 'brief' | 'srsWorkId' | 'mandant'> & { tracks: number; writers: string[] };

const BASE = import.meta.env.VITE_SERVER_URL && !import.meta.env.DEV ? import.meta.env.VITE_SERVER_URL : '/api';

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = useStudio.getState().token;
  const res = await fetch(BASE + path, {
    ...init,
    headers: { ...(init.body && !(init.body instanceof FormData) ? { 'content-type': 'application/json' } : {}), ...(token ? { authorization: `Bearer ${token}` } : {}), ...init.headers },
  });
  if (res.status === 401 && token) useStudio.getState().logout();
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? `${res.status}`);
  return res.json() as Promise<T>;
}

/** Absolute Server-Adresse (für die Bridge, die nicht über den Vite-Proxy geht) */
export const apiBase = () => (BASE.startsWith('http') ? BASE : `${location.origin}${BASE}`);

export const wsUrl = (path: string) => {
  const base = BASE.startsWith('http') ? BASE.replace(/^http/, 'ws') : `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`;
  return base + path;
};

export const api = {
  health: () => req<{ ok: boolean; accessCode?: boolean; login?: { srs: boolean; name: boolean; srsApi: string } }>('/health'),
  srsAuth: (sitzung: string) => req<{ token: string; user: User }>('/auth/srs', { method: 'POST', body: JSON.stringify({ sitzung }) }),
  devLogin: (name: string, role: User['role'] = 'producer', city?: string, code?: string) => req<{ token: string; user: User }>('/auth/dev-login', { method: 'POST', body: JSON.stringify({ name, role, city, code }) }),
  me: () => req<{ user: User }>('/auth/me'),
  turn: () => req<TurnCredentials>('/turn'),
  listProjects: () => req<ProjectSummary[]>('/projects'),
  getProject: (id: string) => req<Project>(`/projects/${id}`),
  saveProject: (p: Project) => req<{ ok: boolean }>(`/projects/${p.id}`, { method: 'PUT', body: JSON.stringify(p) }),
  getSplits: (id: string) => req<SplitSheet | null>(`/projects/${id}/splits`),
  saveSplits: (s: SplitSheet) => req<{ ok: boolean }>(`/projects/${s.projectId}/splits`, { method: 'PUT', body: JSON.stringify(s) }),
  contributions: (id: string) => req<Contribution[]>(`/projects/${id}/contributions`),
  addContribution: (id: string, c: Pick<Contribution, 'kind' | 'label' | 'ai'>) => req<Contribution>(`/projects/${id}/contributions`, { method: 'POST', body: JSON.stringify(c) }),
  createWork: (id: string) => req<{ workId: string; simulated: boolean }>(`/projects/${id}/srs/work`, { method: 'POST' }),
  register: (id: string) => req<{ queued: boolean; simulated: boolean }>(`/projects/${id}/srs/register`, { method: 'POST' }),
  async uploadWav(blob: Blob, name: string, projectId: string) {
    const fd = new FormData();
    fd.append('projectId', projectId);
    fd.append('file', new File([blob], name, { type: blob.type || 'audio/wav' }));
    return req<{ id: string }>('/uploads', { method: 'POST', body: fd });
  },
  /** Importierte Datei unverändert hochladen (MP3 bleibt MP3) */
  uploadFile(file: Blob, name: string, mime: string, projectId: string) {
    const fd = new FormData();
    fd.append('projectId', projectId);
    fd.append('file', new File([file], name, { type: mime }));
    return req<{ id: string }>('/uploads', { method: 'POST', body: fd });
  },
  async media(id: string) {
    const token = useStudio.getState().token;
    const res = await fetch(`${BASE}/media/${id}`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Medium ${id}: ${res.status}`);
    return res.arrayBuffer();
  },
};
